import { useEffect, useRef, useState } from 'react';
import { ListTeams } from '@core/api/endpoints/team/list_teams';
import { ListTeamRoles } from '@core/api/endpoints/team/list_team_roles';
import type { Team, TeamRole } from '@core/types/team';
import type { PaginatedRequest } from '@core/types/pagination';
import { Button, Input } from '@react/components';
import { Modal } from './Modal';

type ActorKind = 'team' | 'role';
type PickerTab = 'admin' | 'member' | 'search';

interface RunAsActorPickerProps {
  open: boolean;
  kind: ActorKind;
  onClose: () => void;
  onSelect: (id: string, label: string) => void;
}

type TeamSearchRequest = PaginatedRequest & {
  filter?: { name?: string };
  include_secret?: boolean;
  membership_scope?: 'admin' | 'member';
};

async function listScopedTeams(scope: 'admin' | 'member'): Promise<Team[]> {
  const result: Team[] = [];
  let cursor = '';
  do {
    const response = await ListTeams({
      pagination: { limit: 50, cursor, direction: 'forward' },
      view: 2,
      include_secret: true,
      membership_scope: scope,
    } as TeamSearchRequest);
    result.push(...(response.teams?.teams ?? []));
    cursor = response.pagination?.page_info?.has_next ? response.pagination.page_info.next_cursor || '' : '';
  } while (cursor);
  return result;
}

export function RunAsActorPicker({ open, kind, onClose, onSelect }: RunAsActorPickerProps) {
  const [teamSearch, setTeamSearch] = useState('');
  const [tab, setTab] = useState<PickerTab>('admin');
  const [adminTeams, setAdminTeams] = useState<Team[]>([]);
  const [memberTeams, setMemberTeams] = useState<Team[]>([]);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickError, setQuickError] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [roleSearch, setRoleSearch] = useState('');
  const [roles, setRoles] = useState<TeamRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualId, setManualId] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const teamSearchRef = useRef<HTMLInputElement>(null);
  const roleSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setTeamSearch('');
    setTab('admin');
    setSelectedTeam(null);
    setRoleSearch('');
    setManualOpen(false);
    setManualId('');
  }, [open, kind]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      (selectedTeam ? roleSearchRef : tab === 'search' ? teamSearchRef : null)?.current?.focus();
    }, 20);
    return () => window.clearTimeout(timer);
  }, [open, selectedTeam, tab]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setQuickLoading(true);
    setQuickError(false);
    Promise.all([listScopedTeams('admin'), listScopedTeams('member')])
      .then(([admin, member]) => {
        if (cancelled) return;
        const adminIds = new Set(admin.map((team) => team.entity?.id));
        setAdminTeams(admin);
        setMemberTeams(member.filter((team) => !adminIds.has(team.entity?.id)));
      })
      .catch(() => { if (!cancelled) setQuickError(true); })
      .finally(() => { if (!cancelled) setQuickLoading(false); });
    return () => { cancelled = true; };
  }, [open, retryKey]);

  useEffect(() => {
    if (!open || selectedTeam || tab !== 'search') return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setTeamsLoading(true);
      setTeamsError(false);
      try {
        const query = teamSearch.trim();
        const request: TeamSearchRequest = {
          pagination: { limit: 100, cursor: '', direction: 'forward' },
          view: 2,
          include_secret: true,
          ...(query ? { filter: { name: query } } : {}),
        };
        const response = await ListTeams(request);
        if (!cancelled) setTeams(response.teams?.teams ?? []);
      } catch {
        if (!cancelled) { setTeams([]); setTeamsError(true); }
      } finally {
        if (!cancelled) setTeamsLoading(false);
      }
    }, teamSearch ? 300 : 0);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [open, selectedTeam, tab, teamSearch, retryKey]);

  useEffect(() => {
    const teamId = selectedTeam?.entity?.id;
    if (!open || kind !== 'role' || !teamId) return;
    let cancelled = false;
    setRoles([]);
    setRolesLoading(true);
    setRolesError(false);
    (async () => {
      try {
        const result: TeamRole[] = [];
        let cursor = '';
        do {
          const response = await ListTeamRoles(teamId, { pagination: { limit: 100, cursor, direction: 'forward' } });
          if (cancelled) return;
          result.push(...(response.roles?.roles ?? []));
          cursor = response.pagination?.page_info?.has_next ? response.pagination.page_info.next_cursor || '' : '';
        } while (cursor);
        if (!cancelled) setRoles(result);
      } catch {
        if (!cancelled) setRolesError(true);
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, kind, selectedTeam, retryKey]);

  const chooseTeam = (team: Team) => {
    if (!team.entity?.id) return;
    if (kind === 'team') {
      onSelect(team.entity.id, team.name || team.entity.id);
      onClose();
    } else {
      setRoleSearch('');
      setSelectedTeam(team);
    }
  };

  const chooseRole = (role: TeamRole) => {
    if (!role.entity?.id || !selectedTeam) return;
    onSelect(role.entity.id, `${role.name || role.entity.id} · ${selectedTeam.name || selectedTeam.entity?.id}`);
    onClose();
  };

  const visibleRoles = roles.filter((role) => {
    const query = roleSearch.trim().toLocaleLowerCase();
    return !query || `${role.name} ${role.description}`.toLocaleLowerCase().includes(query);
  });
  const displayedTeams = tab === 'admin' ? adminTeams : tab === 'member' ? memberTeams : teams;
  const listLoading = tab === 'search' ? teamsLoading : quickLoading;
  const listError = tab === 'search' ? teamsError : quickError;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={kind === 'team' ? 'Выбрать команду' : 'Выбрать роль'}
      description={kind === 'team' ? 'Выберите команду из быстрых списков или найдите её по названию.' : 'Сначала выберите команду, затем доступную роль.'}
      widthClass="max-w-xl"
      heightClass="h-[min(36rem,90vh)]"
      footer={<Button variant="secondary" onClick={onClose}>Закрыть</Button>}
    >
      <div className="space-y-4">
        {selectedTeam && kind === 'role' ? (
          <>
            <button type="button" onClick={() => setSelectedTeam(null)} className="text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
              ← К списку команд
            </button>
            <div className="text-sm font-medium">{selectedTeam.name}</div>
            <Input ref={roleSearchRef} label="Поиск роли" value={roleSearch} onChange={(event) => setRoleSearch(event.target.value)} placeholder="Название роли" />
            {rolesLoading ? <p className="text-sm text-muted-foreground">Загружаем роли…</p> : rolesError ? (
              <div className="space-y-2 text-sm"><p className="text-destructive">Не удалось получить роли этой команды. Возможно, у вас нет к ним доступа.</p><Button size="sm" variant="secondary" onClick={() => setRetryKey((key) => key + 1)}>Повторить</Button></div>
            ) : visibleRoles.length ? (
              <div className="rounded-md border border-border" aria-label="Роли команды">
                {visibleRoles.map((role) => <button key={role.entity?.id} type="button" onClick={() => chooseRole(role)} className="block w-full border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-muted focus-visible:outline-none focus-visible:bg-muted">
                  <span className="block text-sm font-medium">{role.name || role.entity?.id}</span>
                  {role.description && <span className="block text-xs text-muted-foreground">{role.description}</span>}
                </button>)}
              </div>
            ) : <p className="text-sm text-muted-foreground">{roleSearch ? 'По вашему запросу ролей нет.' : 'В этой команде нет доступных ролей.'}</p>}
          </>
        ) : (
          <>
            <div role="tablist" aria-label="Разделы команд" className="flex gap-1 rounded-md bg-muted p-1">
              {([['admin', 'Управляю'], ['member', 'Мои команды'], ['search', 'Поиск']] as const).map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} className={`flex-1 rounded px-2 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tab === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                  {label}
                </button>
              ))}
            </div>
            {tab === 'search' && <Input ref={teamSearchRef} label="Поиск команды" value={teamSearch} onChange={(event) => setTeamSearch(event.target.value)} placeholder="Название команды" />}
            {listLoading ? <p className="text-sm text-muted-foreground">Загружаем команды…</p> : listError ? (
              <div className="space-y-2 text-sm"><p className="text-destructive">Не удалось загрузить команды.</p><Button size="sm" variant="secondary" onClick={() => setRetryKey((key) => key + 1)}>Повторить</Button></div>
            ) : displayedTeams.length ? (
              <div className="rounded-md border border-border" aria-label="Команды">
                {displayedTeams.map((team) => <button key={team.entity?.id} type="button" onClick={() => chooseTeam(team)} className="block w-full border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-muted focus-visible:outline-none focus-visible:bg-muted">
                  <span className="block text-sm font-medium">{team.name || team.entity?.id}</span>
                  {team.description && <span className="block line-clamp-2 text-xs text-muted-foreground">{team.description}</span>}
                </button>)}
              </div>
            ) : <p className="text-sm text-muted-foreground">{tab === 'search' ? (teamSearch ? 'По вашему запросу команд нет.' : 'Доступные команды не найдены.') : tab === 'admin' ? 'Нет команд, которыми вы управляете. Посмотрите «Мои команды» или воспользуйтесь поиском.' : 'Вы не состоите в других командах. Попробуйте поиск.'}</p>}
            {tab === 'search' && teams.length === 100 && <p className="text-xs text-muted-foreground">Показаны первые 100 команд. Уточните название для точного поиска.</p>}
          </>
        )}

        <div className="border-t border-border pt-3">
          <button type="button" onClick={() => setManualOpen((value) => !value)} className="text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
            {manualOpen ? 'Скрыть ввод ID' : 'У меня есть ID актора'}
          </button>
          {manualOpen && <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1"><Input label={kind === 'team' ? 'ID команды' : 'ID роли'} value={manualId} onChange={(event) => setManualId(event.target.value)} placeholder="UUID актора" /></div>
            <Button onClick={() => { if (manualId.trim()) { onSelect(manualId.trim(), manualId.trim()); onClose(); } }} disabled={!manualId.trim()}>Использовать ID</Button>
          </div>}
        </div>
      </div>
    </Modal>
  );
}
